import React, { useState } from "react";
import { Select, message } from "antd";
import Button from "../../../../../components/button";
import { PlusIcon } from "../../../../../layout/Components/icons";
import { ActivityLogIcon, CloseIcon } from "../../../../../icons";
import "../style.scss";
import { useMembers, useUpdateUnitGroup } from "../../../../../api/unitGroups";
import ConfirmModal from "../../../../../components/ModalLayout/components/ConfirmModal";
import { isPermit } from "../../../../../utils/permissionCheck";

interface RightSideProps {
  unitType: "None" | "Primary" | "Merged";
  refetchUnit: () => void;
  unit?: any;
}

const CALL_TYPE_OPTIONS = [
  { label: "Street cleaning", value: 30016 },
  { label: "Missed collection", value: 30019 },
  { label: "Property damaged(pickitup) (domestic)", value: 30020 },
  { label: "Illegal dumping", value: 30021 },
  { label: "Dead animal removal", value: 30006 },
];

const RightSide: React.FC<RightSideProps> = ({
  unitType,
  unit,
  refetchUnit,
}) => {
  const showAssigneeSection = unitType !== "Merged";
  const { data: members = [] } = useMembers();
  const updateUnitGroup = useUpdateUnitGroup();

  const [modalLoading, setModalLoading] = useState(false);

  const [selectedMembersToAdd, setSelectedMembersToAdd] = useState<number[]>(
    []
  );
  const [openMembers, setOpenMembers] = useState(false);
  const [openCallType, setOpenCallType] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalProps, setModalProps] = useState<any>(null);

  const [existingMembers, setExistingMembers] = useState<any[]>(
    unit?.members ?? []
  );
  const [selectedCallTypes, setSelectedCallTypes] = useState<number[]>(
    () => unit?.call_types?.map((c: any) => c.service_id) ?? []
  );

  // -----------------------------
  // API Helpers
  // -----------------------------
  const handleMembersUpdate = async (newMembers: any[]) => {
    const unitId = unit?.id ?? unit?.pikitup_unit_group_id;
    if (!unitId) {
      message.warning("No unit ID available for update");
      return;
    }

    // Filter out invalid members
    const filteredMembers = newMembers.filter((m) => m?.pikitup_member_id);

    try {
      const payload = {
        action: "update" as const,
        unit_group_id: unitId,
        unit_group_name: unit?.name ?? unit?.unit_group_name ?? "",
        member_ids: filteredMembers.map((m) => m.pikitup_member_id),
        service_ids: selectedCallTypes,
        unit_group_description:
          unit?.description ?? unit?.unit_group_desc ?? "",
        depot_id:
          unit?.depot ?? unit?.depot_id ?? unit?.pikitup_depot_id ?? null,
        escalation_member_ids:
          unit?.owners ??
          unit?.escalated_members?.map((m: any) => m.pikitup_member_id) ??
          [],
      };

      await updateUnitGroup.mutateAsync(payload);
      refetchUnit();
      message.success("Members updated successfully!");
    } catch (error: any) {
      console.error(error);
      message.error(
        error.response?.data?.message ||
          "Failed to update members. Please try again."
      );
    }
  };

  const handleCallTypeUpdate = async (newCallTypes: number[]) => {
    const unitId = unit?.id ?? unit?.pikitup_unit_group_id;
    if (!unitId) return message.error("Unit ID not found");

    try {
      const payload = {
        action: "update" as const,
        unit_group_id: unitId,
        unit_group_name: unit?.name ?? unit?.unit_group_name ?? "",
        service_ids: newCallTypes,
        member_ids: existingMembers.map((m) => m.pikitup_member_id),
        unit_group_description:
          unit?.description ?? unit?.unit_group_desc ?? "",
        depot_id:
          unit?.depot ?? unit?.depot_id ?? unit?.pikitup_depot_id ?? null,
        escalation_member_ids:
          unit?.owners ??
          unit?.escalated_members?.map((m: any) => m.pikitup_member_id) ??
          [],
      };

      await updateUnitGroup.mutateAsync(payload);
      refetchUnit();
      message.success("Call types updated successfully!");
    } catch (error: any) {
      console.error(error);
      message.error(
        error.response?.data?.message ||
          "Failed to update call types. Please try again."
      );
    }
  };

  // -----------------------------
  // Render Component
  // -----------------------------
  return (
    <>
      <div className="assignee-details-frame">
        {showAssigneeSection && (
          <div className="header-wrapper flex flex-col gap-4">
            {/* Members Section */}
            <div className="flex items-center gap-2">
              <span className="details-card-heading">Members</span>
              {!openMembers && isPermit(4, "edit") && (
                <button
                  className="transparent add_button p-0 hover:bg-transparent border-none rounded-3xl pointer"
                  onClick={() => setOpenMembers(true)}
                >
                  <PlusIcon />
                </button>
              )}
            </div>

            {/* Select New Members */}
            {openMembers && (
              <div className="w-64">
                <Select
                  mode="multiple"
                  allowClear
                  placeholder="Search name or ID"
                  value={selectedMembersToAdd}
                  onChange={(values) => {
                    const valArr = values as number[];
                    setModalProps({
                      title: "Confirm Member Assignment",
                      message:
                        "Are you sure you want to update the member list?",
                      confirmText: "Yes, Update",
                      onConfirm: async () => {
                        const newMembers = members.filter((m) =>
                          valArr.includes(m.pikitup_member_id)
                        );

                        // Merge with existing and deduplicate
                        const mergedMembersMap = new Map<number, any>();
                        [...existingMembers, ...newMembers].forEach((m) => {
                          if (m?.pikitup_member_id) {
                            mergedMembersMap.set(m.pikitup_member_id, m);
                          }
                        });
                        const mergedMembers = Array.from(
                          mergedMembersMap.values()
                        );

                        setExistingMembers(mergedMembers);
                        setSelectedMembersToAdd([]);
                        setOpenMembers(false);
                        await handleMembersUpdate(mergedMembers);
                      },
                    });
                    setModalOpen(true);
                  }}
                  style={{ width: "100%" }}
                  showSearch
                  optionFilterProp="label"
                  options={members.map((m) => ({
                    label: (
                      <div className="flex flex-col">
                        <span className="font-medium">
                          {m.member_first_name} {m.member_surname}
                        </span>
                        <span className="text-xs text-gray-500">
                          {m.pikitup_member_id}
                        </span>
                      </div>
                    ),
                    value: m.pikitup_member_id,
                  }))}
                  open={openMembers}
                  onDropdownVisibleChange={(visible) =>
                    setOpenMembers(visible as boolean)
                  }
                />
              </div>
            )}

            {/* Members List */}
            <div className="max-h-[120px] overflow-auto">
              {existingMembers.map((member, idx) => {
                const disableRemove = existingMembers.length === 1;
                return (
                  <div
                    key={idx}
                    className="assigned-user flex items-center justify-between bg-gray-100 px-3 py-2 rounded-md w-full"
                  >
                    <div className="flex flex-col">
                      <span className="assigned-user-name font-medium text-sm">
                        {member.member_first_name} {member.member_surname}
                      </span>
                      <div className="flex gap-3 mt-2">
                        <span className="assigned-user-id text-xs text-gray-500">
                          ID: {member.pikitup_member_id}
                        </span>
                        <span className="assigned-user-id text-xs text-gray-500">
                          Employee Type: {member.member_type_name}
                        </span>
                      </div>
                    </div>

                    {isPermit(4, "edit") && (
                      <Button
                        icon={<CloseIcon style={{ fontSize: "18px" }} />}
                        className={`transparent p-0 hover:bg-transparent ${
                          disableRemove
                            ? "opacity-50 cursor-not-allowed"
                            : "cursor-pointer"
                        }`}
                        disabled={disableRemove}
                        onClick={() => {
                          if (disableRemove) {
                            message.warning("At least one member is required.");
                            return;
                          }
                          const updated = existingMembers.filter(
                            (m) =>
                              m.pikitup_member_id !== member.pikitup_member_id
                          );
                          setModalProps({
                            title: "Confirm Remove Member",
                            message:
                              "Are you sure you want to remove this member?",
                            confirmText: "Remove",
                            onConfirm: async () => {
                              setExistingMembers(updated);
                              await handleMembersUpdate(updated);
                            },
                          });
                          setModalOpen(true);
                        }}
                      />
                    )}
                  </div>
                );
              })}
            </div>

            {/* Call Type Section */}
            <div className="header-wrapper flex flex-col gap-4 mt-6">
              <div className="flex items-center gap-2">
                <span className="details-card-heading">Call Type</span>
                {!openCallType && isPermit(4, "edit") && (
                  <button
                    className="transparent add_button p-0 hover:bg-transparent border-none rounded-3xl pointer"
                    onClick={() => setOpenCallType(true)}
                  >
                    <PlusIcon />
                  </button>
                )}
              </div>

              {openCallType && (
                <div className="w-64">
                  <Select
                    mode="multiple"
                    allowClear
                    placeholder="Search Call Types"
                    value={selectedCallTypes}
                    onChange={(values) => {
                      const valArr = values as number[];
                      setModalProps({
                        title: "Confirm Call Type Update",
                        message:
                          "Are you sure you want to update the call type list?",
                        confirmText: "Yes, Update",
                        onConfirm: async () => {
                          setSelectedCallTypes(valArr);
                          await handleCallTypeUpdate(valArr);
                        },
                      });
                      setModalOpen(true);
                    }}
                    style={{ width: "100%" }}
                    showSearch
                    optionFilterProp="label"
                    options={CALL_TYPE_OPTIONS.map((opt) => ({
                      label: (
                        <div className="flex flex-col">
                          <span className="font-medium">{opt.label}</span>
                          <span className="text-xs text-gray-500">
                            {opt.value}
                          </span>
                        </div>
                      ),
                      value: opt.value,
                    }))}
                    open={openCallType}
                    onDropdownVisibleChange={(visible) =>
                      setOpenCallType(visible as boolean)
                    }
                  />
                </div>
              )}

              {/* Call Types List */}
              {selectedCallTypes.map((ctId) => {
                const ct = CALL_TYPE_OPTIONS.find((c) => c.value === ctId);
                if (!ct) return null;
                const disableRemove = selectedCallTypes.length === 1;

                return (
                  <div
                    key={ctId}
                    className="assigned-user flex items-center justify-between bg-gray-100 px-3 py-2 rounded-md w-full"
                  >
                    <div className="flex flex-col">
                      <span className="assigned-user-name font-medium text-sm">
                        {ct.label}
                      </span>
                      <div className="flex gap-3 mt-2">
                        <span className="assigned-user-id text-xs text-gray-500">
                          Code: {ct.value}
                        </span>
                      </div>
                    </div>
                    {isPermit(4, "edit") && (
                      <Button
                        icon={<CloseIcon style={{ fontSize: "18px" }} />}
                        className={`transparent p-0 hover:bg-transparent ${
                          disableRemove
                            ? "opacity-50 cursor-not-allowed"
                            : "cursor-pointer"
                        }`}
                        disabled={disableRemove}
                        onClick={() => {
                          if (disableRemove) {
                            message.warning(
                              "At least one call type is required."
                            );
                            return;
                          }
                          const updatedCallTypes = selectedCallTypes.filter(
                            (id) => id !== ctId
                          );
                          setModalProps({
                            title: "Confirm Call Type Removal",
                            message:
                              "Are you sure you want to remove this call type?",
                            confirmText: "Yes, Remove",
                            onConfirm: async () => {
                              setSelectedCallTypes(updatedCallTypes);
                              await handleCallTypeUpdate(updatedCallTypes);
                            },
                          });
                          setModalOpen(true);
                        }}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Activity Log */}
        {/* Activity Log */}
        {unit?.activity_logs && unit.activity_logs.length > 0 && (
          <div className="activity-log-frame mt-6">
            <span className="details-card-heading">Activity Log</span>

            <div className="activity-log-list flex flex-col gap-3 mt-3 max-h-96 overflow-y-auto">
              {unit?.activity_logs && unit.activity_logs.length > 0 ? (
                unit.activity_logs.map((log: any, index: number) => {
                  // derive readable status change
                  let statusLabel = "";
                  switch (log.unit_group_new_status_flg) {
                    case 1:
                      statusLabel = "Activated";
                      break;
                    case 0:
                      statusLabel = "Deactivated";
                      break;
                    case -1:
                      statusLabel = "Deleted";
                      break;
                    default:
                      statusLabel = "Updated";
                  }

                  return (
                    <div
                      key={log.unit_group_status_hist_id || index}
                      className="activity-card flex flex-col gap-2 p-3 rounded-md shadow-sm bg-gray-50"
                    >
                      <div className="flex items-start gap-2">
                        <span className="activity-log-icon">
                          <ActivityLogIcon />
                        </span>

                        <div className="activity-content-wrapper flex justify-between flex-col">
                          <span className="activity-content text-sm font-medium">
                            {log.remarks || "No remarks available"}
                          </span>

                          {/* <div className="flex justify-between items-center text-xs text-gray-500 mt-1">
                          <span>Status: {statusLabel}</span>
                          <span>
                            Log ID: {log.unit_group_status_hist_id ?? "N/A"}
                          </span>
                        </div> */}
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-gray-500 text-sm italic">
                  No activity logs found.
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Confirm Modal */}
      {modalOpen && (
        <ConfirmModal
          open={modalOpen}
          title={modalProps?.title ?? ""}
          message={modalProps?.message}
          confirmText={modalProps?.confirmText}
          loading={modalLoading}
          onClose={() => {
            if (!modalLoading) {
              setModalOpen(false);
              setModalProps(null);
            }
          }}
          onConfirm={async () => {
            if (modalProps?.onConfirm) {
              try {
                setModalLoading(true);
                await modalProps.onConfirm();
              } finally {
                setModalLoading(false);
                setModalOpen(false);
                setModalProps(null);
              }
            }
          }}
        />
      )}
    </>
  );
};

export default RightSide;
